import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {FaTimes} from 'react-icons/fa';
import './FloatingYoutubePlayer.css';

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
const fit = box => {
  const width = clamp(box.width, Math.min(240, window.innerWidth - 16), window.innerWidth - 16);
  const height = clamp(box.height, Math.min(160, window.innerHeight - 16), window.innerHeight - 16);
  return {...box, width, height, x: clamp(box.x, 8, window.innerWidth - width - 8), y: clamp(box.y, 8, window.innerHeight - height - 8)};
};

export default function FloatingYoutubePlayer({video, onClose}) {
  const [box, setBox] = useState(() => {
    const width = Math.min(480, window.innerWidth - 24);
    return fit({x: window.innerWidth - width - 12, y: 70, width, height: width * 9 / 16 + 76});
  });
  const gesture = useRef(null);
  const closeRef = useRef(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    closeRef.current.focus();
    return () => { if (previousFocus?.isConnected) previousFocus.focus(); };
  }, []);
  useEffect(() => {
    const resize = () => setBox(previous => fit(previous));
    const key = event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); onClose(); }
    };
    window.addEventListener('resize', resize);
    window.addEventListener('keydown', key, true);
    return () => { window.removeEventListener('resize', resize); window.removeEventListener('keydown', key, true); };
  }, [onClose]);
  const begin = (event, mode) => {
    if (event.button !== 0) return;
    event.preventDefault();
    gesture.current = {mode, startX: event.clientX, startY: event.clientY, box};
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const move = event => {
    const current = gesture.current;
    if (!current) return;
    const dx = event.clientX - current.startX;
    const dy = event.clientY - current.startY;
    if (current.mode === 'move') setBox(fit({...current.box, x: current.box.x + dx, y: current.box.y + dy}));
    else setBox({...current.box,
      width: clamp(current.box.width + dx, Math.min(240, window.innerWidth - 16), window.innerWidth - current.box.x - 8),
      height: clamp(current.box.height + dy, Math.min(160, window.innerHeight - 16), window.innerHeight - current.box.y - 8)});
  };
  const end = () => { gesture.current = null; };
  const keyboardResize = event => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    setBox(previous => fit({...previous,
      width: previous.width + (event.key === 'ArrowRight' ? 16 : event.key === 'ArrowLeft' ? -16 : 0),
      height: previous.height + (event.key === 'ArrowDown' ? 16 : event.key === 'ArrowUp' ? -16 : 0)}));
  };
  const keyboardMove = event => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    setBox(previous => fit({...previous,
      x: previous.x + (event.key === 'ArrowRight' ? 16 : event.key === 'ArrowLeft' ? -16 : 0),
      y: previous.y + (event.key === 'ArrowDown' ? 16 : event.key === 'ArrowUp' ? -16 : 0)}));
  };
  return createPortal(<section role="dialog" aria-label={'YouTube: ' + video.title} className="floating-youtube" style={{left: box.x, top: box.y, width: box.width, height: box.height}}>
    <div className="floating-youtube-header">
      <span className="floating-youtube-drag" role="button" tabIndex={0} aria-label="Przesuń odtwarzacz" title="Przeciągnij okno lub użyj strzałek" onKeyDown={keyboardMove} onPointerDown={event => begin(event, 'move')} onPointerMove={move} onPointerUp={end} onLostPointerCapture={end}>YouTube · {video.title}</span>
      <button ref={closeRef} type="button" aria-label="Zamknij film" onClick={onClose}><FaTimes/></button>
    </div>
    <iframe src={video.src} title={'Film YouTube: ' + video.title} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen referrerPolicy="strict-origin-when-cross-origin"/>
    <div className="floating-youtube-footer"><button type="button" className="floating-youtube-resize" aria-label="Zmień rozmiar odtwarzacza" title="Przeciągnij lub użyj strzałek, aby zmienić rozmiar" onPointerDown={event => begin(event, 'resize')} onPointerMove={move} onPointerUp={end} onLostPointerCapture={end} onKeyDown={keyboardResize}>◢</button></div>
  </section>, document.getElementById('chwala-app') || document.body);
}
